const WhitepaperPage = () => {
  return (
    <div className="info-page">
      <div className="info-page__header">
        <h1 className="info-page__title">Whitepaper</h1>
        <p className="info-page__subtitle">Milligram Platform Whitepaper &mdash; Version 1.0, March 2026</p>
      </div>

      <div className="info-page__sections">
        {/* 1. Abstract */}
        <section className="info-page__section">
          <h3>1. Abstract</h3>
          <p>
            Milligram (milligram.tv) is a decentralized platform on Ethereum that brings gold to the blockchain at milligram-scale resolution. By bridging tokenized physical gold (PAXG) into Milligram (MG) tokens &mdash; where 1 PAXG equals 31,103.5 MG &mdash; the platform enables micro-denomination gold ownership, trading, and settlement. A companion governance token, Milligram TV (MGTV), introduces a deflationary burn mechanism that rewards long-term participants with a 10x fee reduction on all vault and exchange operations. Together, the Gold Vault, on-chain order book exchange, and dual-token economics create a complete system for accessible, transparent, and fee-efficient gold-backed digital assets.
          </p>
        </section>

        {/* 2. Introduction */}
        <section className="info-page__section">
          <h3>2. Introduction</h3>
          <h4>The Problem</h4>
          <p>
            Gold has been a store of value for millennia, yet access to fractional gold ownership remains limited. Traditional gold markets impose high minimums, custodial friction, and opaque fee structures. Even tokenized gold products like PAXG, while a significant step forward, still denominate in troy ounces &mdash; a unit too large for everyday micro-transactions or fine-grained portfolio allocation.
          </p>
          <h4>The Solution</h4>
          <p>
            Milligram solves this by converting tokenized troy ounces into milligram-denominated units on Ethereum. One troy ounce of gold equals exactly 31,103.5 milligrams &mdash; a physical constant that anchors the MG token's conversion rate. Users deposit PAXG into the Gold Vault to mint MG tokens, trade them on an on-chain order book exchange, and redeem them back to PAXG at any time. The platform's dual fee tier &mdash; full rate and discounted rate via MGTV burning &mdash; aligns incentives between active traders, long-term holders, and the health of the ecosystem.
          </p>
        </section>

        {/* 3. Token Definitions */}
        <section className="info-page__section">
          <h3>3. Token Definitions</h3>

          <h4>MG &mdash; Milligram</h4>
          <div className="info-page__detail">
            <div className="info-page__detail-row"><span>Name</span><strong>Milligram</strong></div>
            <div className="info-page__detail-row"><span>Symbol</span><strong>MG</strong></div>
            <div className="info-page__detail-row"><span>Decimals</span><strong>18</strong></div>
            <div className="info-page__detail-row"><span>Total Supply</span><strong>10,000,000,000</strong></div>
            <div className="info-page__detail-row"><span>Purpose</span><strong>Milligram-denominated gold unit</strong></div>
            <div className="info-page__detail-row"><span>Initial Distribution</span><strong>Entire supply to GoldVault</strong></div>
          </div>
          <p>
            MG is the platform's core asset. Each MG token represents one milligram of gold, backed by PAXG locked in the Gold Vault. MG tokens enter circulation only through the vault's mint function and can be returned via redemption.
          </p>

          <h4>MGTV &mdash; Milligram TV</h4>
          <div className="info-page__detail">
            <div className="info-page__detail-row"><span>Name</span><strong>Milligram TV</strong></div>
            <div className="info-page__detail-row"><span>Symbol</span><strong>MGTV</strong></div>
            <div className="info-page__detail-row"><span>Decimals</span><strong>18</strong></div>
            <div className="info-page__detail-row"><span>Total Supply</span><strong>1,000,000 (deflationary)</strong></div>
            <div className="info-page__detail-row"><span>Purpose</span><strong>Governance &amp; fee discount</strong></div>
          </div>
          <p>
            MGTV is the platform's governance and utility token. Holding MGTV grants access to discounted fee rates across the vault and exchange. MGTV is burned on each discounted transaction, making it permanently deflationary.
          </p>

          <h4>PAXG &mdash; Pax Gold (External)</h4>
          <div className="info-page__detail">
            <div className="info-page__detail-row"><span>Name</span><strong>Pax Gold</strong></div>
            <div className="info-page__detail-row"><span>Symbol</span><strong>PAXG</strong></div>
            <div className="info-page__detail-row"><span>Decimals</span><strong>18</strong></div>
            <div className="info-page__detail-row"><span>Purpose</span><strong>Tokenized physical gold (1:1)</strong></div>
          </div>
          <p>
            PAXG is an existing ERC-20 token backed 1:1 by physical gold bars held in Brink's vaults. It serves as the collateral layer for the Milligram platform.
          </p>

          <h4>USDT &mdash; Tether USD (External)</h4>
          <div className="info-page__detail">
            <div className="info-page__detail-row"><span>Name</span><strong>Tether USD</strong></div>
            <div className="info-page__detail-row"><span>Symbol</span><strong>USDT</strong></div>
            <div className="info-page__detail-row"><span>Decimals</span><strong>18</strong></div>
            <div className="info-page__detail-row"><span>Purpose</span><strong>USD-pegged stablecoin</strong></div>
          </div>
        </section>

        {/* 4. Gold Vault Mechanics */}
        <section className="info-page__section">
          <h3>4. Gold Vault Mechanics</h3>
          <p>
            The Gold Vault is the gateway between PAXG and MG. It holds the platform's entire MG supply at deployment and releases MG only through minting. Conversely, it accepts MG back during redemption and returns the equivalent PAXG.
          </p>

          <h4>Conversion Rate</h4>
          <div className="info-page__code">
            1 troy ounce = 31,103.5 milligrams{'\n'}
            1 PAXG = 31,103.5 MG{'\n'}
            {'\n'}
            On-chain constants:{'\n'}
            MG_PER_PAXG_X10 = 311035{'\n'}
            FEE_DENOMINATOR  = 100000
          </div>

          <h4>Minting (PAXG &rarr; MG)</h4>
          <p>A user deposits PAXG into the vault and receives MG in return, minus a fee.</p>
          <div className="info-page__code">
            mgAmount  = (paxgAmount &times; 311035) / 10{'\n'}
            feeAmount = (mgAmount &times; feeRate) / 100000{'\n'}
            mgToUser  = mgAmount &minus; feeAmount
          </div>
          <p>
            <strong>Example &mdash; Minting 1 PAXG (full fee):</strong> mgAmount = 31,103.5 MG, fee = 1,147.72 MG, user receives 29,955.78 MG.
          </p>

          <h4>Redemption (MG &rarr; PAXG)</h4>
          <p>A user returns MG to the vault and receives PAXG in return, minus a fee.</p>
          <div className="info-page__code">
            paxgAmount = (mgAmount &times; 10) / 311035{'\n'}
            feeAmount  = (paxgAmount &times; feeRate) / 100000{'\n'}
            paxgToUser = paxgAmount &minus; feeAmount
          </div>
          <p>
            <strong>Example &mdash; Redeeming 31,103.5 MG (discounted):</strong> paxgAmount = 1.0 PAXG, fee = 0.00369 PAXG, user receives 0.99631 PAXG, 31.1035 MGTV burned.
          </p>
        </section>

        {/* 5. Exchange Mechanics */}
        <section className="info-page__section">
          <h3>5. Exchange Mechanics</h3>
          <p>
            The Milligram Exchange is an on-chain limit order book that enables peer-to-peer trading of any deposited token pair (e.g., MG/USDT, MG/PAXG). It uses an internal ledger for balance tracking, ensuring atomic settlement of all trades.
          </p>

          <h4>Deposit &amp; Withdraw</h4>
          <p>Before trading, users deposit tokens into the exchange's internal ledger. Withdrawals return tokens to the user's wallet.</p>

          <h4>Order Lifecycle</h4>
          <p>
            <strong>Making an Order:</strong> A maker specifies what token they want (tokenGet), how much (amountGet), what they're offering (tokenGive), and how much (amountGive). The ratio defines the price.
          </p>
          <p>
            <strong>Filling an Order:</strong> Any user can fill an open order. The filler provides the tokenGet amount plus a fee. The order filler pays all fees &mdash; the maker receives their full requested amount.
          </p>
          <div className="info-page__code">
            Filler's tokenGet  -= (amountGet + feeAmount){'\n'}
            Maker's tokenGet   += amountGet{'\n'}
            Treasury's tokenGet += feeAmount{'\n'}
            Maker's tokenGive  -= amountGive{'\n'}
            Filler's tokenGive += amountGive
          </div>
        </section>

        {/* 6. Fee & Burn Model */}
        <section className="info-page__section">
          <h3>6. Fee &amp; Burn Model</h3>
          <p>
            The platform employs a dual-tier fee structure across both the Gold Vault and the Exchange. Users who hold and burn MGTV receive a 10x fee discount.
          </p>
          <div className="info-page__detail">
            <div className="info-page__detail-row"><span>Full Fee Rate</span><strong>3.69%</strong></div>
            <div className="info-page__detail-row"><span>Discounted Fee Rate</span><strong>0.369%</strong></div>
            <div className="info-page__detail-row"><span>MGTV Burn Rate</span><strong>0.1%</strong></div>
            <div className="info-page__detail-row"><span>FEE_DENOMINATOR</span><strong>100,000</strong></div>
          </div>
          <p>
            The discount requires the user to hold sufficient MGTV and to have approved the contract to burn it. The MGTV burned is permanently destroyed &mdash; removed from the user's balance and from the token's total supply.
          </p>

          <h4>Deflationary Pressure</h4>
          <p>
            Every discounted transaction permanently removes MGTV from circulation. With an initial supply of 1,000,000 MGTV and a burn rate of 0.1% per transaction, sustained platform usage creates compounding scarcity. As MGTV supply decreases, remaining tokens become more valuable, fee discount access becomes more exclusive, and holders face a strategic choice between burning for immediate savings and holding for future value.
          </p>
        </section>

        {/* 7. Governance & Guardian */}
        <section className="info-page__section">
          <h3>7. Governance &amp; Guardian</h3>
          <p>
            The platform uses a Guardian model for administrative control. The Guardian is a designated Ethereum address with authority over configurable protocol parameters.
          </p>
          <div className="info-page__table">
            <div className="info-page__table-row info-page__table-row--header">
              <span>Contract</span>
              <span>Function</span>
              <span>Description</span>
            </div>
            <div className="info-page__table-row">
              <span>GoldVault</span>
              <span>setMgtvBurnRate</span>
              <span>Update burn rate</span>
            </div>
            <div className="info-page__table-row">
              <span>Exchange</span>
              <span>setFeeAccount</span>
              <span>Change treasury</span>
            </div>
            <div className="info-page__table-row">
              <span>Exchange</span>
              <span>transferGuardian</span>
              <span>Transfer role</span>
            </div>
          </div>
          <p>
            Fee rates (fullFeeRate and discountFeeRate) are immutable after deployment &mdash; only the burn rate is adjustable. Constructor-level validation ensures fees cannot exceed 10%.
          </p>
        </section>

        {/* 8. Security */}
        <section className="info-page__section">
          <h3>8. Security</h3>
          <p>
            Both GoldVault and Exchange inherit OpenZeppelin's ReentrancyGuard. All external-facing functions use the nonReentrant modifier. All state-modifying functions follow the Checks-Effects-Interactions (CEI) pattern.
          </p>
          <p>
            All financial calculations use Solidity's native uint256 integer arithmetic with no floating-point math. Precision is maintained through scaled constants (MG_PER_PAXG_X10 = 311035, FEE_DENOMINATOR = 100000). Solidity 0.8.0+ provides built-in overflow/underflow protection.
          </p>
          <p>
            Input validation includes zero-amount checks, zero-address checks, fee rate caps at deployment (max 10%), and redemption requires non-zero PAXG output.
          </p>
        </section>

        {/* 9. Smart Contract Architecture */}
        <section className="info-page__section">
          <h3>9. Smart Contract Architecture</h3>

          <h4>Token.sol</h4>
          <p>Standard ERC-20 with burn capabilities: transfer, approve, transferFrom, burn, burnFrom. All four platform tokens are deployed from this same contract.</p>

          <h4>GoldVault.sol</h4>
          <p>The mint/redeem engine. Holds entire MG supply, distributes via mint(), accepts returns via redeem(). Dual fee tier with MGTV burn discount. Guardian-controlled burn rate.</p>

          <h4>Exchange.sol</h4>
          <p>On-chain limit order book with internal balance ledger. depositToken/withdrawToken for balance management, makeOrder/cancelOrder/fillOrder for the full order lifecycle. Filler-pays-fee model with automatic MGTV discount detection.</p>

          <h4>Deployment Flow</h4>
          <div className="info-page__code">
            1. Deploy MGTV token (1,000,000 supply){'\n'}
            2. Deploy MG token (10,000,000,000 supply){'\n'}
            3. Deploy GoldVault with PAXG, MG, MGTV refs{'\n'}
            4. Transfer entire MG supply to GoldVault{'\n'}
            5. Deploy Exchange with fee rates &amp; guardian
          </div>
        </section>

        {/* 10. Roadmap */}
        <section className="info-page__section">
          <h3>10. Roadmap</h3>
          <h4>Phase 1 &mdash; Sepolia Testnet (Current)</h4>
          <p>Full contract deployment on Sepolia, frontend application, community testing and feedback, security review and audit preparation.</p>
          <h4>Phase 2 &mdash; Ethereum Mainnet</h4>
          <p>Mainnet deployment with canonical PAXG and USDT, guardian assignment to multisig, treasury activation. Integrated token swap via Uniswap V2 Router for instant exchange between ETH, USDT, and PAXG.</p>
          <h4>Future Considerations</h4>
          <p>DAO governance migration, additional trading pairs, Layer 2 deployment, advanced order types (partial fills, stop-loss), formal third-party security audit.</p>
        </section>

        {/* 11. Disclaimer */}
        <section className="info-page__section">
          <h3>11. Disclaimer</h3>
          <p>
            This whitepaper is provided for informational purposes only and does not constitute financial, investment, legal, or tax advice. The Milligram platform and its associated tokens (MG, MGTV) are experimental software deployed on the Ethereum blockchain.
          </p>
          <p>
            Risks include: smart contract risk, market risk, regulatory risk, counterparty risk (reliance on external PAXG/USDT), liquidity risk, and technology risk. Users should conduct their own research and consult qualified professionals. Participation is at the user's own risk.
          </p>
        </section>
      </div>
    </div>
  )
}

export default WhitepaperPage
